import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock, Search, Save, Loader2, ExternalLink, MapPin, Phone, Globe, Star,
  ArrowLeft, Filter, Download, Settings, Trash2, CheckCircle2,
  X, ChevronLeft, ChevronRight, Users, LogOut,
  Calendar, Instagram, Facebook, Linkedin, Wand2, Sparkles, Copy, Send, FileText, Mail,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "tcsw_admin_pw"; // shared with /admin
const LEAD_TAB_KEY = "tcsw_leads_tab";

const STATUS_STYLES = {
  new:              "bg-purple/20 text-purple-hi border-purple-hi/40",
  hot_lead:         "bg-red-500/15 text-red-300 border-red-400/50",
  contacted:        "bg-blue-500/15 text-blue-300 border-blue-400/40",
  follow_up:        "bg-amber-500/15 text-amber-300 border-amber-400/40",
  quoted:           "bg-cyan-500/15 text-cyan-300 border-cyan-400/40",
  won:              "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
  lost:             "bg-white/10 text-white/50 border-white/15",
  not_interested:   "bg-white/10 text-white/45 border-white/15",
  do_not_contact:   "bg-red-500/10 text-red-400 border-red-500/30",
};

function scoreColour(s) {
  if (s == null) return "bg-white/10 text-white/60";
  if (s >= 85) return "bg-emerald-500/20 text-emerald-300 border-emerald-400/40";
  if (s >= 70) return "bg-gold/20 text-gold border-gold/40";
  if (s >= 50) return "bg-cyan-500/20 text-cyan-300 border-cyan-400/40";
  return "bg-white/10 text-white/50 border-white/15";
}

export default function AdminLeads() {
  const [pw, setPw] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [tab, setTab] = useState(() => sessionStorage.getItem(LEAD_TAB_KEY) || "search");
  useEffect(() => { sessionStorage.setItem(LEAD_TAB_KEY, tab); }, [tab]);

  const headers = useMemo(() => ({ "X-Admin-Password": pw }), [pw]);

  useEffect(() => {
    if (!pw) return;
    axios.post(`${API}/admin/verify`, {}, { headers })
      .then(() => setAuthed(true))
      .catch(() => { sessionStorage.removeItem(STORAGE_KEY); setPw(""); });
  }, [pw, headers]);

  const login = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Password": input } });
      sessionStorage.setItem(STORAGE_KEY, input);
      setPw(input); setAuthed(true);
    } catch {
      toast.error("Incorrect password");
    }
  };
  const logout = () => { sessionStorage.removeItem(STORAGE_KEY); setPw(""); setAuthed(false); };

  if (!authed) {
    return (
      <div className="min-h-screen bg-ink-950 text-white flex items-center justify-center p-6">
        <form onSubmit={login} className="card-dark p-8 w-full max-w-sm" data-testid="leads-login">
          <div className="flex items-center gap-3">
            <Lock size={16} className="text-gold" />
            <div className="font-display text-lg">Local Lead Finder</div>
          </div>
          <input
            type="password"
            data-testid="leads-pw-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Admin password"
            className="w-full mt-5 bg-ink-800 border border-white/10 px-4 py-3 rounded-sm outline-none focus:border-gold text-sm"
            autoFocus
          />
          <button className="btn-gold w-full justify-center mt-4">Sign in</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink-950 text-white">
      <header className="border-b border-white/10 sticky top-0 z-30 bg-ink-950/95 backdrop-blur">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/admin" className="text-white/60 hover:text-gold flex items-center gap-2 text-sm" data-testid="back-to-admin">
            <ArrowLeft size={14} /> Admin
          </Link>
          <div className="h-6 w-px bg-white/10" />
          <div className="font-display text-lg">Local Signs & Wraps Lead Finder</div>
          <div className="flex gap-1 ml-6 border border-white/10 rounded-sm overflow-hidden">
            {[
              { key: "search",     label: "Search",     Icon: Search },
              { key: "saved",      label: "Saved",      Icon: Users },
              { key: "follow_ups", label: "Follow-ups", Icon: Calendar },
              { key: "export",     label: "Export",     Icon: Download },
              { key: "settings",   label: "Settings",   Icon: Settings },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                data-testid={`leads-tab-${key}`}
                onClick={() => setTab(key)}
                className={`px-3 py-1.5 text-xs uppercase tracking-[0.18em] transition-colors flex items-center gap-2 ${tab === key ? "bg-gold text-black" : "text-white/60 hover:text-gold"}`}
              >
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>
          <button onClick={logout} className="ml-auto text-white/50 hover:text-red-400 text-xs flex items-center gap-1"><LogOut size={12} /> Sign out</button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {tab === "search" && <SearchTab headers={headers} />}
        {tab === "saved" && <SavedTab headers={headers} />}
        {tab === "follow_ups" && <SavedTab headers={headers} initialFilters={{ follow_up_due: true }} title="Follow-ups due" />}
        {tab === "export" && <ExportTab headers={headers} />}
        {tab === "settings" && <SettingsTab headers={headers} />}
      </main>
    </div>
  );
}


/* ─────────────────────── SEARCH TAB ─────────────────────── */

function SearchTab({ headers }) {
  const [form, setForm] = useState({
    keyword: "", city: "Toronto", radius_m: 5000, limit: 20,
    min_rating: "", min_review_count: "",
    only_with_phone: false, only_with_website: false, skip_saved: true,
    service_focus: "storefront_signs",
  });
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    axios.get(`${API}/leads/categories`, { headers })
      .then(({ data }) => setCategoryOptions(data.items || []))
      .catch(() => {});
    axios.get(`${API}/settings`, { headers })
      .then(({ data }) => {
        const s = data.settings || {};
        setForm((f) => ({
          ...f,
          city: s.lead_search_default_city?.value || f.city,
          radius_m: s.lead_search_default_radius_m?.value || f.radius_m,
          limit: s.lead_search_default_limit?.value || f.limit,
        }));
      }).catch(() => {});
  }, [headers]);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!form.keyword.trim()) { toast.error("Enter a keyword or business type"); return; }
    setLoading(true); setSelected(new Set());
    try {
      const payload = {
        keyword: form.keyword.trim(),
        city: form.city.trim() || undefined,
        radius_m: Number(form.radius_m),
        limit: Number(form.limit),
        only_with_phone: form.only_with_phone,
        only_with_website: form.only_with_website,
        skip_saved: form.skip_saved,
        service_focus: form.service_focus || undefined,
      };
      if (form.min_rating) payload.min_rating = Number(form.min_rating);
      if (form.min_review_count) payload.min_review_count = Number(form.min_review_count);
      const { data } = await axios.post(`${API}/leads/search`, payload, { headers });
      setResults(data.items || []);
      if (!data.items?.length) toast.message("No results — try broader keywords or a different city.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleOne = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected((s) => s.size === results.length ? new Set() : new Set(results.map(r => r.google_place_id)));

  const bulkSave = async () => {
    const picks = results.filter(r => selected.has(r.google_place_id));
    if (!picks.length) { toast.error("Select at least one lead."); return; }
    try {
      const { data } = await axios.post(`${API}/leads/save`, {
        leads: picks, service_focus: form.service_focus, on_duplicate: "skip",
      }, { headers });
      toast.success(`Saved ${data.saved_count} · Skipped ${data.skipped_count} duplicate${data.skipped_count === 1 ? "" : "s"}`);
      // Mark saved rows in the table
      setResults((rs) => rs.map(r => selected.has(r.google_place_id) ? { ...r, already_saved: true } : r));
      setSelected(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div>
      <form onSubmit={runSearch} className="card-dark p-6 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="search-form">
        <div className="col-span-2 md:col-span-2">
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/50">Keyword / business type</label>
          <input
            data-testid="search-keyword"
            value={form.keyword}
            onChange={(e) => setForm({ ...form, keyword: e.target.value })}
            list="lead-categories-dl"
            placeholder="e.g. restaurants, auto shops, dentists"
            className="w-full mt-1 bg-ink-800 border border-white/10 px-3 py-2 rounded-sm text-sm outline-none focus:border-gold"
          />
          <datalist id="lead-categories-dl">
            {categoryOptions.map((c) => <option key={c.keyword} value={c.keyword}>{c.label}</option>)}
          </datalist>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/50">City</label>
          <input data-testid="search-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full mt-1 bg-ink-800 border border-white/10 px-3 py-2 rounded-sm text-sm outline-none focus:border-gold" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/50">Radius (m)</label>
          <input type="number" min="500" max="50000" step="500" data-testid="search-radius" value={form.radius_m} onChange={(e) => setForm({ ...form, radius_m: e.target.value })} className="w-full mt-1 bg-ink-800 border border-white/10 px-3 py-2 rounded-sm text-sm outline-none focus:border-gold" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/50">Limit (max 20)</label>
          <input type="number" min="1" max="20" data-testid="search-limit" value={form.limit} onChange={(e) => setForm({ ...form, limit: e.target.value })} className="w-full mt-1 bg-ink-800 border border-white/10 px-3 py-2 rounded-sm text-sm outline-none focus:border-gold" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/50">Min rating</label>
          <input type="number" min="0" max="5" step="0.1" value={form.min_rating} onChange={(e) => setForm({ ...form, min_rating: e.target.value })} className="w-full mt-1 bg-ink-800 border border-white/10 px-3 py-2 rounded-sm text-sm outline-none focus:border-gold" placeholder="e.g. 4.0" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/50">Min reviews</label>
          <input type="number" min="0" value={form.min_review_count} onChange={(e) => setForm({ ...form, min_review_count: e.target.value })} className="w-full mt-1 bg-ink-800 border border-white/10 px-3 py-2 rounded-sm text-sm outline-none focus:border-gold" placeholder="e.g. 20" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/50">Service focus</label>
          <select value={form.service_focus} onChange={(e) => setForm({ ...form, service_focus: e.target.value })} className="w-full mt-1 bg-ink-800 border border-white/10 px-3 py-2 rounded-sm text-sm outline-none focus:border-gold">
            <option value="storefront_signs">Storefront signs</option>
            <option value="window_graphics">Window graphics</option>
            <option value="vehicle_wraps">Vehicle wraps</option>
            <option value="wall_murals">Wall murals</option>
            <option value="fleet_graphics">Fleet graphics</option>
            <option value="general_signs">General signs</option>
          </select>
        </div>
        <div className="col-span-2 md:col-span-4 flex flex-wrap items-center gap-5 pt-2">
          <label className="flex items-center gap-2 text-xs text-white/70"><input type="checkbox" checked={form.only_with_phone} onChange={(e) => setForm({ ...form, only_with_phone: e.target.checked })} /> Phone required</label>
          <label className="flex items-center gap-2 text-xs text-white/70"><input type="checkbox" checked={form.only_with_website} onChange={(e) => setForm({ ...form, only_with_website: e.target.checked })} /> Website required</label>
          <label className="flex items-center gap-2 text-xs text-white/70"><input type="checkbox" checked={form.skip_saved} onChange={(e) => setForm({ ...form, skip_saved: e.target.checked })} /> Skip already-saved</label>
          <button type="submit" disabled={loading} data-testid="search-submit" className="btn-gold ml-auto disabled:opacity-50">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            {loading ? "Searching…" : "Search leads"}
          </button>
        </div>
      </form>

      {results.length > 0 && (
        <div className="mt-6" data-testid="search-results">
          <div className="flex items-center gap-3 mb-3">
            <div className="text-sm text-white/70">{results.length} result{results.length === 1 ? "" : "s"}</div>
            {selected.size > 0 && (
              <div className="ml-auto flex items-center gap-2">
                <span className="text-xs text-white/60">{selected.size} selected</span>
                <button onClick={bulkSave} data-testid="bulk-save-btn" className="btn-gold !py-2 !px-3 !text-[11px]">
                  <Save size={12} /> Save selected
                </button>
              </div>
            )}
          </div>
          <div className="card-dark overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-[10px] uppercase tracking-[0.18em] text-white/50">
                <tr>
                  <th className="px-3 py-2 text-left w-8"><input type="checkbox" checked={selected.size === results.length} onChange={toggleAll} /></th>
                  <th className="px-3 py-2 text-left">Business</th>
                  <th className="px-3 py-2 text-left">Category</th>
                  <th className="px-3 py-2 text-left">Contact</th>
                  <th className="px-3 py-2 text-left">Rating</th>
                  <th className="px-3 py-2 text-left">Score</th>
                  <th className="px-3 py-2 text-left">Pitch</th>
                  <th className="px-3 py-2 text-right"> </th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.google_place_id} className={`border-t border-white/5 ${selected.has(r.google_place_id) ? "bg-gold/5" : ""}`} data-testid={`search-row-${r.google_place_id}`}>
                    <td className="px-3 py-3"><input type="checkbox" checked={selected.has(r.google_place_id)} onChange={() => toggleOne(r.google_place_id)} disabled={r.already_saved} /></td>
                    <td className="px-3 py-3">
                      <div className="font-medium text-white">{r.business_name}</div>
                      <div className="text-white/50 text-xs mt-0.5 flex items-center gap-1">
                        <MapPin size={10} /> {r.address || r.city || "—"}
                      </div>
                      {r.already_saved && <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-400 mt-1">✓ Already saved</div>}
                    </td>
                    <td className="px-3 py-3 text-white/70">{r.category || "—"}</td>
                    <td className="px-3 py-3">
                      {r.phone && <div className="flex items-center gap-1 text-xs text-white/85"><Phone size={10} /> {r.phone}</div>}
                      {r.website && <a href={r.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-cyan-300 hover:text-cyan-200 mt-0.5"><Globe size={10} /> Website</a>}
                      {r.google_maps_url && <a href={r.google_maps_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-white/60 hover:text-gold mt-0.5"><ExternalLink size={10} /> Google Maps</a>}
                    </td>
                    <td className="px-3 py-3 text-white/70 text-xs">
                      {r.rating ? <div className="flex items-center gap-1"><Star size={10} className="text-gold" /> {r.rating} <span className="text-white/40">({r.review_count})</span></div> : "—"}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 border rounded-sm text-[10px] uppercase tracking-[0.18em] font-mono ${scoreColour(r.lead_score)}`}>{r.lead_score}</span>
                    </td>
                    <td className="px-3 py-3 text-white/60 text-xs max-w-xs">{r.suggested_service}</td>
                    <td className="px-3 py-3 text-right">
                      <button
                        onClick={async () => {
                          try {
                            const { data } = await axios.post(`${API}/leads/save`, { leads: [r], service_focus: form.service_focus, on_duplicate: "skip" }, { headers });
                            if (data.saved_count > 0) toast.success("Lead saved.");
                            else toast.message("Duplicate — already saved.");
                            setResults((rs) => rs.map(x => x.google_place_id === r.google_place_id ? { ...x, already_saved: true } : x));
                          } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
                        }}
                        disabled={r.already_saved}
                        data-testid={`save-one-${r.google_place_id}`}
                        className="btn-ghost !py-1.5 !px-2 !text-[10px] disabled:opacity-30"
                      ><Save size={11} /> Save</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {!loading && results.length === 0 && (
        <div className="card-dark p-16 mt-6 text-center text-white/50" data-testid="search-empty">
          <Search size={32} className="mx-auto text-white/20 mb-4" />
          Enter a keyword and click Search to find leads via Google Places.
        </div>
      )}
    </div>
  );
}


/* ─────────────────────── SAVED TAB ─────────────────────── */

function SavedTab({ headers, initialFilters = {}, title = "" }) {
  const followUpsMode = initialFilters.follow_up_due === true;
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "", city: "", search: "", min_score: "", ...initialFilters });
  const [selected, setSelected] = useState(new Set());
  const [statuses, setStatuses] = useState([]);
  const [detailLead, setDetailLead] = useState(null);

  useEffect(() => {
    axios.get(`${API}/leads/statuses`, { headers })
      .then(({ data }) => setStatuses(data.items || []))
      .catch(() => {});
  }, [headers]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
      if (filters.status) params.set("status", filters.status);
      if (filters.city) params.set("city", filters.city);
      if (filters.search) params.set("search", filters.search);
      if (filters.min_score) params.set("min_score", filters.min_score);
      if (filters.follow_up_due) params.set("follow_up_due", "true");
      const { data } = await axios.get(`${API}/leads/saved?${params.toString()}`, { headers });
      setRows(data.items || []); setTotal(data.total || 0);
    } catch (e) { toast.error("Failed to load leads"); }
    finally { setLoading(false); }
  }, [headers, page, filters]);

  useEffect(() => { load(); }, [load]);

  const toggleOne = (id) => setSelected((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const toggleAll = () => setSelected((s) => s.size === rows.length ? new Set() : new Set(rows.map(r => r.id)));

  const bulkStatus = async (status) => {
    if (!selected.size) return;
    try {
      await axios.post(`${API}/leads/bulk-update`, { ids: [...selected], status }, { headers });
      toast.success(`Marked ${selected.size} as ${status.replace("_", " ")}.`);
      setSelected(new Set()); await load();
    } catch (e) { toast.error("Bulk update failed"); }
  };
  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} lead${selected.size === 1 ? "" : "s"}? This can't be undone.`)) return;
    try {
      await axios.post(`${API}/leads/bulk-delete`, { ids: [...selected] }, { headers });
      toast.success(`Deleted ${selected.size} lead${selected.size === 1 ? "" : "s"}.`);
      setSelected(new Set()); await load();
    } catch (e) { toast.error("Bulk delete failed"); }
  };

  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <div>
      {title && (
        <div className="mb-4">
          <div className="eyebrow gold-bar">Follow-ups</div>
          <h2 className="font-display text-2xl mt-2">{title}</h2>
          <p className="text-white/50 text-xs mt-1">Leads with a follow-up date on or before today, excluding won/lost/do-not-contact.</p>
        </div>
      )}
      {/* Filters */}
      <div className="card-dark p-4 flex flex-wrap gap-3 items-center" data-testid="saved-filters">
        <Filter size={14} className="text-white/40" />
        <input placeholder="Search name / phone / website" value={filters.search} onChange={(e) => { setFilters({ ...filters, search: e.target.value }); setPage(0); }} className="bg-ink-800 border border-white/10 px-3 py-2 rounded-sm text-sm outline-none focus:border-gold min-w-[220px]" />
        <select value={filters.status} onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(0); }} className="bg-ink-800 border border-white/10 px-3 py-2 rounded-sm text-sm outline-none focus:border-gold">
          <option value="">All statuses</option>
          {statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <input placeholder="City" value={filters.city} onChange={(e) => { setFilters({ ...filters, city: e.target.value }); setPage(0); }} className="bg-ink-800 border border-white/10 px-3 py-2 rounded-sm text-sm outline-none focus:border-gold w-32" />
        <input type="number" placeholder="Min score" min="0" max="100" value={filters.min_score} onChange={(e) => { setFilters({ ...filters, min_score: e.target.value }); setPage(0); }} className="bg-ink-800 border border-white/10 px-3 py-2 rounded-sm text-sm outline-none focus:border-gold w-24" />
        <div className="ml-auto text-xs text-white/50">{total} lead{total === 1 ? "" : "s"} · page {page + 1} / {maxPage + 1}</div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="card-dark bg-purple/5 border-purple-hi/40 p-3 mt-3 flex flex-wrap items-center gap-2" data-testid="bulk-bar">
          <span className="text-xs text-white uppercase tracking-[0.18em]">{selected.size} selected</span>
          <select onChange={(e) => e.target.value && bulkStatus(e.target.value)} defaultValue="" className="bg-ink-800 border border-white/10 px-2 py-1.5 text-xs rounded-sm">
            <option value="">Mark as…</option>
            {statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <button onClick={bulkDelete} data-testid="bulk-delete-btn" className="btn-ghost !py-1.5 !px-3 !text-[11px] border-red-500/40 text-red-300 hover:bg-red-500 hover:text-white ml-auto">
            <Trash2 size={12} /> Delete selected
          </button>
        </div>
      )}

      {/* Table */}
      <div className="card-dark mt-3 overflow-x-auto" data-testid="saved-table">
        {loading ? (
          <div className="p-16 text-center text-white/40"><Loader2 className="animate-spin mx-auto" /></div>
        ) : rows.length === 0 ? (
          <div className="p-16 text-center text-white/50">No saved leads yet. Search + save some.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-[10px] uppercase tracking-[0.18em] text-white/50">
              <tr>
                <th className="px-3 py-2 w-8"><input type="checkbox" checked={selected.size === rows.length && rows.length > 0} onChange={toggleAll} /></th>
                <th className="px-3 py-2 text-left">Business</th>
                <th className="px-3 py-2 text-left">Contact</th>
                <th className="px-3 py-2 text-left">Score</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Follow-up</th>
                <th className="px-3 py-2 text-right"> </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const overdue = followUpsMode && r.follow_up_date && r.follow_up_date < new Date().toISOString().slice(0, 10);
                return (
                <tr key={r.id} className={`border-t border-white/5 hover:bg-white/[0.02] ${overdue ? "bg-red-500/5" : ""}`} data-testid={`lead-row-${r.id}`}>
                  <td className="px-3 py-3"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleOne(r.id)} /></td>
                  <td className="px-3 py-3">
                    <div className="font-medium text-white">{r.business_name}</div>
                    <div className="text-white/50 text-xs mt-0.5">{r.category} · {r.city || "—"}</div>
                  </td>
                  <td className="px-3 py-3 text-xs">
                    {r.phone && <div className="text-white/80">{r.phone}</div>}
                    {r.email && <div className="text-cyan-300">{r.email}</div>}
                    {r.website && <a href={r.website} target="_blank" rel="noreferrer" className="text-white/50 hover:text-gold">{r.website.replace(/^https?:\/\/(www\.)?/, "").slice(0, 30)}</a>}
                  </td>
                  <td className="px-3 py-3"><span className={`inline-flex px-2 py-0.5 border rounded-sm text-[10px] font-mono ${scoreColour(r.lead_score)}`}>{r.lead_score}</span></td>
                  <td className="px-3 py-3"><span className={`inline-flex px-2 py-0.5 border rounded-sm text-[10px] uppercase tracking-[0.18em] ${STATUS_STYLES[r.status] || "bg-white/10 text-white/60"}`}>{(r.status || "new").replace("_", " ")}</span></td>
                  <td className="px-3 py-3 text-xs text-white/60">
                    {r.follow_up_date ? (
                      <span className={overdue ? "text-red-300 font-medium" : ""}>
                        {r.follow_up_date}{overdue ? " · overdue" : ""}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <button onClick={() => setDetailLead(r)} data-testid={`open-lead-${r.id}`} className="btn-ghost !py-1.5 !px-2 !text-[10px]">Open</button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-4 text-xs text-white/50">
        <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="disabled:opacity-30 flex items-center gap-1"><ChevronLeft size={12} /> Prev</button>
        <div>Page {page + 1} of {maxPage + 1}</div>
        <button disabled={page >= maxPage} onClick={() => setPage(p => p + 1)} className="disabled:opacity-30 flex items-center gap-1">Next <ChevronRight size={12} /></button>
      </div>

      {detailLead && <LeadDetailPanel lead={detailLead} headers={headers} onClose={() => setDetailLead(null)} onChanged={load} />}
    </div>
  );
}


/* ─────────────────────── LEAD DETAIL PANEL (slide-over) ─────────────────────── */

function LeadDetailPanel({ lead, headers, onClose, onChanged }) {
  const [current, setCurrent] = useState(lead);
  const [activity, setActivity] = useState([]);
  const [saving, setSaving] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [enriching, setEnriching] = useState(false);
  const [outreachMsg, setOutreachMsg] = useState("");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    axios.get(`${API}/leads/${lead.id}`, { headers })
      .then(({ data }) => {
        setCurrent(data.lead);
        setActivity(data.activity || []);
        setOutreachMsg(data.lead.last_outreach_message || "");
      })
      .catch(() => {});
    axios.get(`${API}/leads/statuses`, { headers })
      .then(({ data }) => setStatuses(data.items || []));
  }, [lead.id, headers]);

  const patch = async (updates) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/leads/${lead.id}`, updates, { headers });
      const { data } = await axios.get(`${API}/leads/${lead.id}`, { headers });
      setCurrent(data.lead); setActivity(data.activity || []);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const deleteLead = async () => {
    if (!window.confirm(`Delete "${current.business_name}"?`)) return;
    try {
      await axios.delete(`${API}/leads/${lead.id}`, { headers });
      toast.success("Deleted.");
      onChanged?.(); onClose();
    } catch (e) { toast.error("Delete failed"); }
  };

  const enrich = async () => {
    if (!current.website) { toast.error("This lead has no website to scan."); return; }
    setEnriching(true);
    try {
      const { data } = await axios.post(`${API}/leads/${lead.id}/enrich`, {}, { headers, timeout: 25000 });
      const found = [];
      if (data.applied?.email) found.push("email");
      if (data.applied?.instagram_url) found.push("Instagram");
      if (data.applied?.facebook_url) found.push("Facebook");
      if (data.applied?.linkedin_url) found.push("LinkedIn");
      toast.success(found.length ? `Found: ${found.join(", ")}` : "No new contact info found on the site.");
      // Refresh
      const { data: refreshed } = await axios.get(`${API}/leads/${lead.id}`, { headers });
      setCurrent(refreshed.lead); setActivity(refreshed.activity || []);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Enrich failed"); }
    finally { setEnriching(false); }
  };

  const generateOutreach = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/leads/${lead.id}/outreach-message`, {}, { headers, timeout: 30000 });
      setOutreachMsg(data.message || "");
      toast.success("Outreach message generated.");
      const { data: refreshed } = await axios.get(`${API}/leads/${lead.id}`, { headers });
      setActivity(refreshed.activity || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setGenerating(false); }
  };

  const copyOutreach = async () => {
    if (!outreachMsg) return;
    try { await navigator.clipboard.writeText(outreachMsg); toast.success("Copied to clipboard."); }
    catch { toast.error("Copy failed"); }
  };

  const sendEmail = () => {
    if (!current.email) { toast.error("No email on file — enrich first or add one manually."); return; }
    const subject = encodeURIComponent(`Quick idea for ${current.business_name}`);
    const body = encodeURIComponent(outreachMsg || "");
    window.open(`mailto:${current.email}?subject=${subject}&body=${body}`, "_blank");
  };

  const convertToQuote = () => {
    // Prefill the /contact quote form via URL params. This lets the shop
    // owner turn a lead into a real quote in one click without a separate
    // quote/invoice system.
    const params = new URLSearchParams({
      from_lead: "1",
      company: current.business_name || "",
      phone: current.phone || "",
      email: current.email || "",
      location: current.city || "",
      message: `Quote request pre-filled from Lead Finder.\n\nSuggested pitch: ${current.suggested_service || ""}`,
    });
    window.open(`/contact?${params.toString()}`, "_blank");
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="lead-detail-panel">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-ink-950 border-l border-white/10 w-full max-w-xl overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-ink-950 border-b border-white/10 p-5 flex items-center gap-3">
          <div className="flex-1">
            <div className="font-display text-lg text-white">{current.business_name}</div>
            <div className="text-white/50 text-xs mt-0.5">{current.category} · {current.city}</div>
          </div>
          <button onClick={onClose} className="p-2 text-white/50 hover:text-white"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-6">
          <section>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Contact</div>
            <div className="space-y-1.5 text-sm">
              {current.phone && <div className="flex items-center gap-2"><Phone size={12} /> {current.phone}</div>}
              {current.email && <div className="flex items-center gap-2 text-cyan-300"><span>@</span> {current.email}</div>}
              {current.website && <a href={current.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-cyan-300 hover:text-cyan-200"><Globe size={12} /> {current.website}</a>}
              {current.google_maps_url && <a href={current.google_maps_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-white/60 hover:text-gold"><ExternalLink size={12} /> Google Maps</a>}
              {current.address && <div className="flex items-center gap-2 text-white/70"><MapPin size={12} /> {current.address}</div>}
              {(current.instagram_url || current.facebook_url || current.linkedin_url) && (
                <div className="flex gap-2 pt-2" data-testid="lead-socials">
                  {current.instagram_url && <a href={current.instagram_url} target="_blank" rel="noreferrer" className="p-1.5 border border-white/10 hover:border-pink-400/60 hover:text-pink-300 rounded-sm text-white/60 transition-colors" title="Instagram"><Instagram size={12} /></a>}
                  {current.facebook_url && <a href={current.facebook_url} target="_blank" rel="noreferrer" className="p-1.5 border border-white/10 hover:border-blue-400/60 hover:text-blue-300 rounded-sm text-white/60 transition-colors" title="Facebook"><Facebook size={12} /></a>}
                  {current.linkedin_url && <a href={current.linkedin_url} target="_blank" rel="noreferrer" className="p-1.5 border border-white/10 hover:border-cyan-400/60 hover:text-cyan-300 rounded-sm text-white/60 transition-colors" title="LinkedIn"><Linkedin size={12} /></a>}
                </div>
              )}
              <button
                onClick={enrich}
                disabled={enriching || !current.website}
                data-testid="enrich-lead-btn"
                className="mt-3 btn-ghost !py-2 !px-3 !text-[11px] border-cyan-400/40 text-cyan-300 hover:bg-cyan-300 hover:text-black disabled:opacity-30"
                title={!current.website ? "Need a website to scan" : "Scan website for email + socials"}
              >
                {enriching ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                {enriching ? "Scanning site…" : (current.enriched_at ? "Re-scan website" : "Find email & socials")}
              </button>
            </div>
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Signals</div>
            <div className="flex gap-4 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">Lead score</div>
                <span className={`inline-flex px-2 py-0.5 border rounded-sm text-xs font-mono mt-1 ${scoreColour(current.lead_score)}`}>{current.lead_score}</span>
              </div>
              {current.rating && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">Rating</div>
                  <div className="text-white text-xs mt-1 flex items-center gap-1"><Star size={11} className="text-gold" /> {current.rating} <span className="text-white/40">({current.review_count})</span></div>
                </div>
              )}
              {current.business_status && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">Business</div>
                  <div className="text-white/70 text-xs mt-1">{current.business_status.replace(/_/g, " ").toLowerCase()}</div>
                </div>
              )}
            </div>
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Suggested service</div>
            <div className="text-sm text-white/80 leading-relaxed">{current.suggested_service}</div>
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Status</div>
            <select value={current.status} onChange={(e) => patch({ status: e.target.value })} data-testid="lead-status-select" className="w-full bg-ink-800 border border-white/10 px-3 py-2 rounded-sm text-sm outline-none focus:border-gold">
              {statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Follow-up date</div>
            <input type="date" value={current.follow_up_date || ""} onChange={(e) => patch({ follow_up_date: e.target.value })} className="w-full bg-ink-800 border border-white/10 px-3 py-2 rounded-sm text-sm outline-none focus:border-gold" />
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Notes</div>
            <textarea rows={4} defaultValue={current.notes || ""} onBlur={(e) => e.target.value !== (current.notes || "") && patch({ notes: e.target.value })} data-testid="lead-notes" placeholder="Add call notes, quote details, pitch angles…" className="w-full bg-ink-800 border border-white/10 px-3 py-2 rounded-sm text-sm outline-none focus:border-gold" />
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Activity ({activity.length})</div>
            <div className="space-y-2 max-h-48 overflow-y-auto text-xs">
              {activity.map((a) => (
                <div key={a.id} className="flex gap-2 text-white/60">
                  <div className="text-white/40 w-28 shrink-0">{new Date(a.created_at).toLocaleString()}</div>
                  <div><span className="text-white/80">{a.activity_type}</span>{a.note ? `: ${a.note}` : ""}</div>
                </div>
              ))}
            </div>
          </section>

          <section data-testid="outreach-section">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2 flex items-center gap-2">
              <Sparkles size={11} className="text-emerald-400" /> AI outreach message
            </div>
            {outreachMsg ? (
              <>
                <textarea
                  value={outreachMsg}
                  onChange={(e) => setOutreachMsg(e.target.value)}
                  rows={6}
                  data-testid="outreach-textarea"
                  className="w-full bg-ink-800 border border-emerald-500/40 px-3 py-2 rounded-sm text-sm outline-none focus:border-emerald-400 leading-relaxed"
                />
                <div className="flex gap-2 mt-2">
                  <button onClick={copyOutreach} data-testid="outreach-copy" className="btn-ghost !py-2 !px-3 !text-[11px]"><Copy size={11} /> Copy</button>
                  <button onClick={sendEmail} data-testid="outreach-send" disabled={!current.email} className="btn-ghost !py-2 !px-3 !text-[11px] border-emerald-400/40 text-emerald-300 hover:bg-emerald-400 hover:text-black disabled:opacity-30"><Send size={11} /> Send now</button>
                  <button onClick={generateOutreach} disabled={generating} data-testid="outreach-regen" className="btn-ghost !py-2 !px-3 !text-[11px] ml-auto disabled:opacity-30">
                    {generating ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
                    Regenerate
                  </button>
                </div>
              </>
            ) : (
              <button
                onClick={generateOutreach}
                disabled={generating}
                data-testid="outreach-generate"
                className="btn-ghost !py-2 !px-3 !text-[11px] border-emerald-400/40 text-emerald-300 hover:bg-emerald-400 hover:text-black disabled:opacity-30"
              >
                {generating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {generating ? "Writing…" : "Generate outreach message"}
              </button>
            )}
          </section>

          <div className="flex gap-2 pt-4 border-t border-white/10">
            <button
              onClick={convertToQuote}
              data-testid="convert-to-quote-btn"
              className="btn-gold !py-2 !px-3 !text-[11px]"
              title="Opens the /contact quote form pre-filled with this lead's details"
            ><FileText size={12} /> Convert to quote</button>
            <button onClick={deleteLead} data-testid="delete-lead-btn" className="ml-auto btn-ghost !py-2 !px-3 !text-[11px] border-red-500/40 text-red-300 hover:bg-red-500 hover:text-white"><Trash2 size={12} /> Delete</button>
          </div>
          {saving && <div className="text-[10px] text-white/40 flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Saving…</div>}
        </div>
      </div>
    </div>
  );
}


/* ─────────────────────── EXPORT TAB ─────────────────────── */

function ExportTab({ headers }) {
  const csvUrl = `${API}/leads/export.csv`;
  const download = async () => {
    try {
      const { data } = await axios.get(csvUrl, { headers, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  return (
    <div className="card-dark p-8" data-testid="export-tab">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-gold/10 rounded-sm"><Download className="text-gold" /></div>
        <div className="flex-1">
          <div className="font-display text-lg text-white">Export leads to CSV</div>
          <p className="text-white/60 text-sm mt-2 max-w-lg">Downloads every saved lead as a spreadsheet with contact, category, score, status, suggested service, and follow-up date columns. Excel + Google Sheets compatible.</p>
          <button onClick={download} data-testid="export-download-btn" className="btn-gold mt-5"><Download size={14} /> Download all leads.csv</button>
        </div>
      </div>
    </div>
  );
}


/* ─────────────────────── SETTINGS TAB ─────────────────────── */

function SettingsTab({ headers }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/settings`, { headers });
      setSettings(data.settings || {});
    } catch { toast.error("Failed to load settings"); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const patch = async (key, value) => {
    try {
      await axios.patch(`${API}/settings/${key}`, { value }, { headers });
      toast.success("Setting saved.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  if (loading || !settings) return <div className="text-white/50 p-8"><Loader2 className="animate-spin mx-auto" /></div>;

  const s = settings;
  const keyPresent = s.google_places_api_key_present?.value;

  return (
    <div className="card-dark p-8 space-y-6" data-testid="settings-tab">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Google Places API</div>
        <div className="flex items-center gap-3">
          {keyPresent ? (
            <><CheckCircle2 className="text-emerald-400" size={16} /> <span className="text-emerald-300 text-sm">Configured</span></>
          ) : (
            <><X className="text-red-400" size={16} /> <span className="text-red-300 text-sm">Missing</span></>
          )}
        </div>
        <p className="text-white/50 text-xs mt-2 max-w-xl leading-relaxed">
          The Places API key is stored in <code className="bg-ink-800 px-1.5 py-0.5 rounded-sm">backend/.env</code> as
          {" "}<code className="bg-ink-800 px-1.5 py-0.5 rounded-sm">GOOGLE_PLACES_API_KEY</code>. Get one from
          {" "}<a href="https://console.cloud.google.com/google/maps-apis/credentials" target="_blank" rel="noreferrer" className="text-cyan-300 hover:text-cyan-200 underline">Google Cloud Console</a>{" "}
          and enable <strong>Places API (New)</strong> on the same project.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SettingField label="Default city" value={s.lead_search_default_city?.value || ""} onSave={(v) => patch("lead_search_default_city", v)} />
        <SettingField label="Default radius (m)" type="number" value={s.lead_search_default_radius_m?.value || 5000} onSave={(v) => patch("lead_search_default_radius_m", Number(v))} />
        <SettingField label="Default limit (1-20)" type="number" value={s.lead_search_default_limit?.value || 20} onSave={(v) => patch("lead_search_default_limit", Number(v))} />
      </div>
    </div>
  );
}

function SettingField({ label, value, type = "text", onSave }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-white/50">{label}</label>
      <input
        type={type}
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => v !== value && onSave(v)}
        className="w-full mt-1 bg-ink-800 border border-white/10 px-3 py-2 rounded-sm text-sm outline-none focus:border-gold"
      />
    </div>
  );
}
